'use client';

// ClarificationQuestions - Main component for displaying clarification questions

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslations } from 'next-intl';
import { BotyIcon } from '@boty/ui';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Chip from '@mui/material/Chip';
import { ClarificationQuestionItem } from './ClarificationQuestionItem';
import { useClarificationQuestions } from './useClarificationQuestions';
import { IntakeQuestionsConfig } from '../intake-questions';
import type { ClarificationQuestionsProps } from './types';

// Number of questions to show initially before "See more"
const INITIAL_QUESTIONS_TO_SHOW = 3;

/**
 * ClarificationQuestions - Main component for the clarification phase
 *
 * Features:
 * - Generates clarification questions from bot content
 * - Displays comprehensiveness score
 * - Shows list of questions with answer inputs
 * - Handles answer submission and skipping
 * - Automatically proceeds to testing when all questions are resolved
 * - RTL support via CSS logical properties
 */
export function ClarificationQuestions({
  botId,
  onComplete,
  onBack,
}: ClarificationQuestionsProps) {
  const t = useTranslations('creator.clarification');

  // Track if we've already triggered completion to prevent multiple calls
  const hasCompletedRef = useRef(false);

  // Track whether to show all questions or just the first 3
  const [showAllQuestions, setShowAllQuestions] = useState(false);

  // Story 7.6: Intake Questions CTA
  const tIntake = useTranslations('creator.intakeQuestions');
  const [intakeDialogOpen, setIntakeDialogOpen] = useState(false);
  const [intakeJustConfigured, setIntakeJustConfigured] = useState(false);

  const {
    questions,
    isLoading,
    isGenerating,
    error,
    generate,
    generateMore,
    submitAnswer,
    skipQuestion,
    reopenQuestion,
    isSubmitting,
    allQuestionsResolved,
    summary,
    hasExistingQuestions,
  } = useClarificationQuestions({ botId });

  // Scroll to top when clarification phase mounts (mobile fix)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-proceed to testing when all questions are resolved
  // Skip auto-proceed when questions were loaded from DB (returning user should review)
  useEffect(() => {
    if (allQuestionsResolved && !hasCompletedRef.current && !hasExistingQuestions) {
      hasCompletedRef.current = true;
      onComplete();
    }
  }, [allQuestionsResolved, onComplete, hasExistingQuestions]);

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error.message || t('errors.generationFailed')}
      </Alert>
    );
  }

  // No questions yet - show generate button
  if (questions.length === 0 && !isGenerating) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          p: { xs: 2, sm: 4 },
          textAlign: 'center',
        }}
      >
        {/* Back button */}
        {onBack && (
          <Box sx={{ alignSelf: 'flex-start', width: '100%', mb: { xs: 0.5, sm: 2 } }}>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              size="small"
            >
              {t('backToContent')}
            </Button>
          </Box>
        )}
        <AutoAwesomeIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main' }} />
        <Typography variant="h6">{t('title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
          {t('subtitle')}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={generate}
          disabled={isGenerating}
          startIcon={
            isGenerating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AutoAwesomeIcon />
            )
          }
        >
          {isGenerating ? t('generating') : t('generateButton')}
        </Button>
        {/* Story 7.6: Intake Questions CTA (also on pre-generate page) */}
        {intakeJustConfigured ? (
          <Chip icon={<CheckCircleIcon />} label={tIntake('ctaConfigured')} color="success" variant="outlined" onClick={() => setIntakeDialogOpen(true)} sx={{ mt: { xs: 1, sm: 2 }, cursor: 'pointer' }} />
        ) : (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 2 },
              mt: { xs: 1, sm: 3 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 1, sm: 2 },
              textAlign: 'start',
              width: '100%',
              maxWidth: 500,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
              <PersonAddIcon color="primary" sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2">{tIntake('ctaTitle')}</Typography>
                <Typography variant="body2" color="text.secondary">{tIntake('ctaDescription')}</Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIntakeDialogOpen(true)}
              sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              {tIntake('ctaButton')}
            </Button>
          </Paper>
        )}

        <Button
          variant="text"
          size="small"
          onClick={() => onComplete()}
          sx={{ mt: { xs: 0.5, sm: 2 }, color: 'text.secondary' }}
        >
          {t('skipPhase')}
        </Button>

        {/* Intake Questions Dialog (must be inside this early-return block) */}
        <Dialog open={intakeDialogOpen} onClose={() => setIntakeDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {tIntake('sectionTitle')}
            <IconButton
              aria-label="close"
              onClick={() => setIntakeDialogOpen(false)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <IntakeQuestionsConfig
              botId={botId}
              intakeEnabled={false}
              intakeCompletionMessage={null}
              intakePromptMessage={null}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIntakeDialogOpen(false)}>
              {tIntake('ctaSkip')}
            </Button>
            <Button variant="contained" onClick={() => { setIntakeDialogOpen(false); setIntakeJustConfigured(true); }}>
              {t('done')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Generating state - Animated Boty mascot with progress
  if (isGenerating) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 200, sm: 400 },
          textAlign: 'center',
          p: { xs: 1.5, sm: 4 },
          width: '100%',
        }}
        role="status"
        aria-live="polite"
      >
        {/* Animated Boty mascot */}
        <Box
          sx={{
            mb: { xs: 1.5, sm: 4 },
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.05)', opacity: 0.9 },
            },
          }}
        >
          <BotyIcon size={120} variant="full" />
        </Box>

        {/* Headline */}
        <Typography
          variant="h5"
          fontWeight="bold"
          gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          {t('generating')}
        </Typography>

        {/* Progress bar */}
        <Box sx={{ width: '100%', maxWidth: 400, mb: 2, px: { xs: 2, sm: 0 } }}>
          <LinearProgress
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main',
                borderRadius: 4,
              },
            }}
          />
        </Box>

        {/* Reassurance text */}
        <Typography variant="body2" color="text.secondary">
          {t('generatingSubtitle')}
        </Typography>

        <Button
          variant="text"
          size="small"
          onClick={() => onComplete()}
          sx={{ mt: 2, color: 'text.secondary' }}
        >
          {t('skipPhase')}
        </Button>
      </Box>
    );
  }

  // If no questions were generated, proceed directly
  if (questions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('noQuestions')}
        </Alert>
        <Button variant="contained" onClick={() => onComplete()}>
          {t('summary.proceedToTesting')}
        </Button>
      </Box>
    );
  }

  // Show questions list
  const pendingQuestions = questions.filter((q) => q.status === 'pending');
  const resolvedQuestions = questions.filter((q) => q.status !== 'pending');

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, pb: 12 }}>
      {/* Back button */}
      {onBack && (
        <Box sx={{ mb: { xs: 1, sm: 2 } }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            size="small"
          >
            {t('backToContent')}
          </Button>
        </Box>
      )}

      {/* Title and subtitle */}
      <Typography variant="h6" gutterBottom>
        {t('title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 1.5, sm: 3 } }}>
        {t('subtitle')}
      </Typography>

      {/* Progress indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 2 } }}>
        <Typography variant="body2" color="text.secondary">
          {t('question.resolvedCount', { resolved: summary.answered + summary.skipped, total: summary.total })}
        </Typography>
        {summary.totalCharactersAdded > 0 && (
          <Typography variant="caption" color="text.disabled">
            ({t('question.charactersAdded', { count: summary.totalCharactersAdded })})
          </Typography>
        )}
      </Box>

      {/* Pending questions - show first 3, then "See more" */}
      {pendingQuestions.slice(0, INITIAL_QUESTIONS_TO_SHOW).map((question) => (
        <ClarificationQuestionItem
          key={question.id}
          question={question}
          onSubmitAnswer={submitAnswer}
          onSkip={skipQuestion}
          isSubmitting={isSubmitting}
        />
      ))}

      {/* Additional pending questions (hidden by default) */}
      {pendingQuestions.length > INITIAL_QUESTIONS_TO_SHOW && (
        <>
          <Collapse in={showAllQuestions}>
            {pendingQuestions.slice(INITIAL_QUESTIONS_TO_SHOW).map((question) => (
              <ClarificationQuestionItem
                key={question.id}
                question={question}
                onSubmitAnswer={submitAnswer}
                onSkip={skipQuestion}
                isSubmitting={isSubmitting}
              />
            ))}
          </Collapse>

          {/* See more / See less button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Button
              variant="text"
              onClick={() => setShowAllQuestions(!showAllQuestions)}
              endIcon={showAllQuestions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ color: 'text.secondary' }}
            >
              {showAllQuestions
                ? t('showLess')
                : t('showMore', { count: pendingQuestions.length - INITIAL_QUESTIONS_TO_SHOW })}
            </Button>
          </Box>
        </>
      )}

      {/* Resolved questions (collapsed section) */}
      {resolvedQuestions.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('question.resolvedSection', { count: resolvedQuestions.length })}
          </Typography>
          {resolvedQuestions.map((question) => (
            <ClarificationQuestionItem
              key={question.id}
              question={question}
              onSubmitAnswer={submitAnswer}
              onSkip={skipQuestion}
              onReopen={reopenQuestion}
              isSubmitting={isSubmitting}
            />
          ))}
        </Box>
      )}

      {/* Generate more questions CTA */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
        <Button
          variant="outlined"
          onClick={generateMore}
          disabled={isGenerating}
          startIcon={
            isGenerating ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AutoAwesomeIcon />
            )
          }
        >
          {isGenerating ? t('generating') : t('generateMoreButton')}
        </Button>
      </Box>

      {/* Story 7.6: Intake Questions CTA */}
      {intakeJustConfigured ? (
        <Chip icon={<CheckCircleIcon />} label={tIntake('ctaConfigured')} color="success" variant="outlined" onClick={() => setIntakeDialogOpen(true)} sx={{ mt: 2, cursor: 'pointer' }} />
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mt: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
            <PersonAddIcon color="primary" sx={{ flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2">{tIntake('ctaTitle')}</Typography>
              <Typography variant="body2" color="text.secondary">{tIntake('ctaDescription')}</Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIntakeDialogOpen(true)}
            sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            {tIntake('ctaButton')}
          </Button>
        </Paper>
      )}

      {/* Intake Questions Dialog */}
      <Dialog open={intakeDialogOpen} onClose={() => setIntakeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {tIntake('sectionTitle')}
          <IconButton
            aria-label="close"
            onClick={() => setIntakeDialogOpen(false)}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <IntakeQuestionsConfig
            botId={botId}
            intakeEnabled={false}
            intakeCompletionMessage={null}
            intakePromptMessage={null}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntakeDialogOpen(false)}>
            {tIntake('ctaSkip')}
          </Button>
          <Button variant="contained" onClick={() => { setIntakeDialogOpen(false); setIntakeJustConfigured(true); }}>
            {t('done')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sticky floating CTA at bottom */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          // Use logical properties so the bar spans full width in both LTR and RTL.
          // Plain `left: 0; right: 0` gets flipped by stylis-plugin-rtl and can shift the
          // bar off-screen in some Hebrew layouts.
          insetInlineStart: 0,
          insetInlineEnd: 0,
          zIndex: 1100,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          px: 2,
          py: 2,
        }}
      >
        <Box sx={{ maxWidth: 700, mx: 'auto', display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => onComplete()}
            startIcon={<PlayArrowIcon />}
          >
            {t('continueAction')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default ClarificationQuestions;
